import { useState, useMemo } from 'react'
import type { AdjustmentDeltas, PositionPnL } from '@/types/api'
import {
  parseDecimalString,
  formatCurrency,
} from '@/lib/chart-transformers'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TrendingUp, TrendingDown, ChevronDown, ChevronRight, Minus } from 'lucide-react'

interface DeltaComparisonTableProps {
  data?: AdjustmentDeltas
  baselinePositions?: PositionPnL[]
  adjustedPositions?: PositionPnL[]
}

interface DeltaRow {
  id: string
  adjustmentName: string
  baselinePnL: number
  adjustedPnL: number
  deltaPnL: number
  deltaPercentage: number
  isPositive: boolean
  isSignificant: boolean
  keyDrivers: Array<{
    symbol: string
    contribution: number
  }>
}

export function DeltaComparisonTable({
  data,
  baselinePositions,
  adjustedPositions,
}: DeltaComparisonTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  // Transform data into table rows
  const tableData: DeltaRow[] = useMemo(() => {
    if (!data) return []

    const deltaPnL = parseDecimalString(data.delta_absolute)
    const deltaPercentage = parseDecimalString(data.delta_percentage)

    // Calculate key drivers from position-level deltas if available
    const keyDrivers: Array<{ symbol: string; contribution: number }> = []

    if (baselinePositions && adjustedPositions) {
      const baselineMap = new Map(baselinePositions.map(p => [p.symbol, parseDecimalString(p.absolutePnL)]))

      for (const adjPos of adjustedPositions) {
        const baselinePnL = baselineMap.get(adjPos.symbol) || 0
        const adjustedPnL = parseDecimalString(adjPos.absolutePnL)
        const contribution = adjustedPnL - baselinePnL

        if (Math.abs(contribution) > 0.01) {
          keyDrivers.push({
            symbol: adjPos.symbol,
            contribution,
          })
        }
      }

      // Sort by absolute contribution
      keyDrivers.sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution))
    }

    return [
      {
        id: 'primary',
        adjustmentName: data.comparison || 'Adjusted vs Baseline',
        baselinePnL: 0,
        adjustedPnL: deltaPnL,
        deltaPnL,
        deltaPercentage,
        isPositive: deltaPnL >= 0,
        isSignificant: Math.abs(deltaPercentage) > 10,
        keyDrivers: keyDrivers.slice(0, 5),
      },
    ]
  }, [data, baselinePositions, adjustedPositions])

  const columns: ColumnDef<DeltaRow>[] = useMemo(() => [
    {
      accessorKey: 'adjustmentName',
      header: 'Adjustment',
      cell: ({ getValue }) => (
        <span className="font-medium">{getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'deltaPnL',
      header: 'Delta P&L',
      cell: ({ getValue }) => {
        const value = getValue() as number
        return (
          <span className={`font-mono font-medium ${value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {value >= 0 ? '+' : ''}{formatCurrency(value)}
          </span>
        )
      },
    },
    {
      accessorKey: 'deltaPercentage',
      header: 'Delta %',
      cell: ({ getValue }) => {
        const value = getValue() as number
        return (
          <Badge variant={value >= 0 ? 'default' : 'destructive'} className="font-mono">
            {value >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {Math.abs(value).toFixed(2)}%
          </Badge>
        )
      },
    },
    {
      accessorKey: 'isSignificant',
      header: 'Impact',
      cell: ({ getValue }) => {
        const isSignificant = getValue() as boolean
        return isSignificant ? (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            Significant
          </Badge>
        ) : (
          <Badge variant="outline" className="text-gray-500">
            <Minus className="h-3 w-3 mr-1" />
            Minor
          </Badge>
        )
      },
    },
    {
      id: 'actions',
      header: 'Details',
      cell: ({ row }) => {
        const hasDrivers = row.original.keyDrivers.length > 0
        if (!hasDrivers) return null

        const isExpanded = expandedRows.has(row.id)
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newExpanded = new Set(expandedRows)
              if (newExpanded.has(row.id)) {
                newExpanded.delete(row.id)
              } else {
                newExpanded.add(row.id)
              }
              setExpandedRows(newExpanded)
            }}
            className="h-8 px-2"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            <span className="ml-1 text-xs">
              {row.original.keyDrivers.length} drivers
            </span>
          </Button>
        )
      },
    },
  ], [expandedRows])

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (!data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Adjustment Delta Comparison</CardTitle>
          <CardDescription>Baseline vs adjusted portfolio P&L comparison</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
            <Minus className="h-10 w-10 text-gray-300 mb-2" />
            <p className="text-gray-500">No adjustments selected</p>
            <p className="text-gray-400 text-sm mt-1">
              Select adjustment scenarios to see comparison
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Adjustment Delta Comparison</CardTitle>
        <CardDescription>
          Baseline vs adjusted portfolio P&L comparison
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map(row => (
              <>
                <TableRow
                  key={row.id}
                  className={row.original.isSignificant ? 'bg-amber-50/50' : undefined}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {expandedRows.has(row.id) && row.original.keyDrivers.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="bg-gray-50/50 p-4">
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">Key Contributing Positions:</p>
                        <div className="flex flex-wrap gap-2">
                          {row.original.keyDrivers.map((driver, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className={`font-mono ${driver.contribution >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
                            >
                              {driver.symbol}: {driver.contribution >= 0 ? '+' : ''}
                              {formatCurrency(driver.contribution)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
